import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import V2Providers from './V2Providers';
import V2Routes from './V2Routes';
import V2UpdateOverlay from '../components/V2UpdateOverlay';
import { runV2BootUpdates } from '../platform/updater';
import { registerAndGetToken, setupPushTokenRefreshListener } from '../platform/notifications';
import { injectSafeAreaVars } from '../platform/safeArea';
import { registerBackHandler, exitApp } from '../platform/navigation';
import { tryMigrateLegacySession } from '../services/sessionMigration';
import { syncPushTokenIfNeeded } from '../services/deviceToken';
import { setV2BearerToken } from '../services/client';
import { clearSession, getCachedSession, saveSession } from '../services/user';
import { v2Theme } from '../design/theme';
import { v2MuiTheme } from '../design/muiTheme';

async function applyPushToken(session, token) {
  const next = { ...(session || {}), pushToken: token };
  await saveSession(next);
  if (next.mobile && next.token) {
    setV2BearerToken(next.token);
    await syncPushTokenIfNeeded({ mobile: next.mobile, deviceToken: token });
  }
  return next;
}

function useV2Boot() {
  const [ready, setReady] = useState(false);
  const [initialSession, setInitialSession] = useState(null);
  const [updatePercent, setUpdatePercent] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setupPushTokenRefreshListener(async (token) => {
      const current = await getCachedSession();
      await applyPushToken(current, token);
    });

    async function boot() {
      await runV2BootUpdates((p) => {
        if (!cancelled) setUpdatePercent(p);
      });
      if (cancelled) return;

      injectSafeAreaVars();
      let session = await getCachedSession();
      if (!session?.token) {
        const migrated = await tryMigrateLegacySession();
        if (migrated) session = migrated;
      }
      if (session && typeof session === 'object' && session.mobile && !session.token) {
        const { pushToken } = session;
        if (pushToken) {
          await saveSession({ pushToken });
          session = { pushToken };
        } else {
          await clearSession();
          session = null;
        }
      }
      if (!cancelled) {
        setUpdatePercent(null);
        setInitialSession(session);
        setReady(true);
      }
      registerAndGetToken().then(async (token) => {
        if (token) await applyPushToken(session, token);
      });
    }

    boot();
    return () => { cancelled = true; };
  }, []);

  return { ready, initialSession, updatePercent };
}

function V2AppInner() {
  const { ready, initialSession, updatePercent } = useV2Boot();

  const backRef = useRef(null);
  useEffect(() => {
    backRef.current = registerBackHandler(({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else exitApp();
    });
    return () => backRef.current?.();
  }, []);

  if (updatePercent != null) {
    return <V2UpdateOverlay percent={updatePercent} />;
  }

  if (!ready) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ bgcolor: v2Theme.palette.background }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <V2Providers initialSession={initialSession}>
      <V2Routes />
    </V2Providers>
  );
}

export default function V2App() {
  return (
    <ThemeProvider theme={v2MuiTheme}>
      <V2AppInner />
    </ThemeProvider>
  );
}
